// Copyright (c) Meta Platforms, Inc. and affiliates.
// All rights reserved.

// This source code is licensed under the license found in the
// LICENSE file in the root directory of this source tree.

import { Tensor } from "onnxruntime-web";

// Convert the onnx model mask prediction to ImageData
export function arrayToImageData(input, width, height) {
    const [r, g, b, a] = [0, 114, 189, 255]; // the masks's blue color
    const arr = new Uint8ClampedArray(4 * width * height).fill(0);
    for (let i = 0; i < input.length; i++) {

        // Threshold the onnx model mask prediction at 0.0
        // This is equivalent to thresholding the mask using predictor.model.mask_threshold
        // in python
        if (input[i] > 0.0) {
            arr[4 * i + 0] = r;
            arr[4 * i + 1] = g;
            arr[4 * i + 2] = b;
            arr[4 * i + 3] = a;
        }
    }
    return new ImageData(arr, height, width);
}

// Use a Canvas element to produce an image from ImageData
function imageDataToImage(imageData) {
    const canvas = imageDataToCanvas(imageData);
    const image = new Image();
    image.src = canvas.toDataURL();
    return image;
}

// Canvas elements can be created from ImageData
function imageDataToCanvas(imageData) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    ctx?.putImageData(imageData, 0, 0);
    return canvas;
}

// Convert the onnx model mask output to an HTMLImageElement
export function onnxMaskToImage(input, width, height) {
    return imageDataToImage(arrayToImageData(input, width, height));
}

export function onnxMaskToCanvas(input, width, height) {
    return imageDataToCanvas(arrayToImageData(input, width, height));
}

// Helper function for handling image scaling needed for SAM
export function calcImageScale (image) {
    // Input images to SAM must be resized so the longest side is 1024
    const LONG_SIDE_LENGTH = 1024;
    let w = image.naturalWidth;
    let h = image.naturalHeight;
    const samScale = LONG_SIDE_LENGTH / Math.max(h, w);
    return { height: h, width: w, samScale };
}

export function modelData ({ points, tensor, modelScale }) {
    const imageEmbedding = {
        data: tensor.data,
        dims: tensor.dims,
        type: tensor.type
    };
    let pointCoords;
    let pointLabels;
    let pointCoordsTensor;
    let pointLabelsTensor;

    // Check there are input click prompts
    if (points) {
        let n = points.length;

        // If there is no box input, a single padding point with
        // label -1 and coordinates (0.0, 0.0) should be concatenated
        // so initialize the array to support (n + 1) points.
        pointCoords = new Float32Array(2 * (n + 1));
        pointLabels = new Float32Array(n + 1);

        // Add clicks and scale to what SAM expects
        for (let i = 0; i < n; i++) {
            pointCoords[2 * i] = points[i][0] * modelScale.width * modelScale.samScale;
            pointCoords[2 * i + 1] = points[i][1] * modelScale.height * modelScale.samScale;
            pointLabels[i] = points[i][2];
        }

        // Add in the extra point/label when only clicks and no box
        // The extra point is at (0, 0) with label -1
        pointCoords[2 * n] = 0.0;
        pointCoords[2 * n + 1] = 0.0;
        pointLabels[n] = -1.0;

        // Create the tensor data
        pointCoordsTensor = {
            data: pointCoords,
            dims: [1, n + 1, 2],
            type: "float32"
        };
        pointLabelsTensor = {
            data: pointLabels,
            dims: [1, n + 1],
            type: "float32"
        };
    }
    const imageSizeTensor = {
        data: new Float32Array([
            modelScale.height,
            modelScale.width,
        ]),
        dims: [2],
        type: "float32"
    };

    if (pointCoordsTensor === undefined || pointLabelsTensor === undefined)
        return;

    // There is no previous mask, so default to an empty tensor
    const maskInput = {
        data: new Float32Array(256 * 256),
        dims: [1, 1, 256, 256],
        type: "float32"
    };
    // There is no previous mask, so default to 0
    const hasMaskInput = {
        data: new Float32Array([0]),
        dims: [1],
        type: "float32"
    };

    return {
        image_embeddings: imageEmbedding,
        point_coords: pointCoordsTensor,
        point_labels: pointLabelsTensor,
        orig_im_size: imageSizeTensor,
        mask_input: maskInput,
        has_mask_input: hasMaskInput,
    };
}

export function samPointsAreSimilar(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;

    for (let i = 0; i < arr1.length; i++) {
        // Directly compare the third (last) element of each inner array
        if (arr1[i][2] !== arr2[i][2]) {
            return false;
        }
    }

    return true;
}
